import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/skip';
import 'rxjs/add/operator/takeUntil';
import {Injectable} from '@angular/core';
import {Effect, Actions} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {Observable} from 'rxjs/Observable';
import {DocumentService} from '../services/document';
import * as fromRoot from '../reducers';
import * as document from '../actions/document';
import * as auth from '../../core/actions/authentication';
import {of} from 'rxjs/observable/of';
import {saveAs} from 'file-saver';
import { DownloadedDocument } from 'app/modules/document/models/downloaded-document';
import { ErrorModel } from '../../core/models/error-model';

@Injectable()
export class DocumentEffects {

  @Effect()
  uploadFile$: Observable<Action> = this.actions$
    .ofType(document.UPLOAD_FILE)
    .map((a: document.UploadFileAction) => a.payload)
    .switchMap(doc => {
      const errorMessage = 'Error uploading file';
      return this.documentService.upload(doc.file, doc.id, doc.filename)
        .map(id => new document.UploadFileCompleteAction(id))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e,errorMessage ))));
    });

  @Effect()
  attachMetaData$: Observable<Action> = this.actions$
    .ofType(document.ATTACH_METADATA_TO_FILE)
    .map((a: document.AttachMetadataToFileAction) => a.payload)
    .switchMap(doc => {
      const errorMessage = 'Error updating file metadata';
      return this.documentService.upsertMetadata(doc)
        .map(id => new document.AttachMetadataToFileCompleteAction(id))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  attachMetadataComplete$: Observable<Action> = this.actions$
    .ofType(document.ATTACH_METADATA_TO_FILE_COMPLETE)
    .map(() => {
        return new document.RetrieveAllActiveAction();
    });

  @Effect()
  downloadFile$: Observable<Action> = this.actions$
    .ofType(document.DOWNLOAD_FILE)
    .map((a: document.DownloadFileAction) => a.payload)
    .switchMap(d => {
      const errorMessage = 'Error downloading file';
      return this.documentService.download(d.id)
        .map(file => new document.DownloadFileCompleteAction(new DownloadedDocument(file, d.value)))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  downloadFileComplete$: Observable<Action> = this.actions$
    .ofType(document.DOWNLOAD_FILE_COMPLETE)
    .map((a: document.DownloadFileCompleteAction) => a.payload)
    .switchMap(doc => {
      this.saveFile(doc.file, doc.filename);
      return of({ type: 'NO_ACTION' });
    });

  @Effect()
  getFileUrl$: Observable<Action> = this.actions$
    .ofType(document.GET_FILE_VIEW_URL)
    .map((a: document.GetFileViewUrlAction) => a.payload)
    .switchMap(id => {
      const errorMessage = 'Error downloading file';
      return this.documentService.download(id)
        .map(file => {
            return new document.GetFileViewUrlCompleteAction(this.getFileUrl(file))
            })
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  getFileUrlComplete$: Observable<Action> = this.actions$
    .ofType(document.DOWNLOAD_FILE_COMPLETE)
    .map((a: document.DownloadFileCompleteAction) => a.payload)
    .switchMap(doc => {
      this.saveFile(doc.file, doc.filename);
      return of({ type: 'NO_ACTION' });
    });

  @Effect()
  retrieveMetaData$: Observable<Action> = this.actions$
    .ofType(document.RETRIEVE_METADATA)
    .map((a: document.RetrieveMetadataAction) => a.payload)
    .switchMap(id => {
      const errorMessage = 'Error retrieving metadata for file';
      return this.documentService.retrieve(id)
        .map(meta => new document.RetrieveMetadataCompleteAction(meta))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  retrieveMetaDataForIds$: Observable<Action> = this.actions$
    .ofType(document.RETRIEVE_METADATA_FOR_IDS)
    .map((a: document.RetrieveMetadataForIdsAction) => a.payload)
    .switchMap(ids => {
      const errorMessage = 'Error retrieving metadata for ids';
      return this.documentService.retrieveForIds(ids)
        .map(meta => new document.RetrieveMetadataForIdsCompleteAction(meta))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  retrieveActiveMetadata$: Observable<Action> = this.actions$
    .ofType(document.RETRIEVE_ALL_ACTIVE)
    .switchMap(() => {
      const errorMessage = 'Error retrieving active documents';
      return this.documentService.retrieveActive()
        .map(meta => new document.RetrieveAllActiveCompleteAction(meta))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  retrieveDeletedMetadata$: Observable<Action> = this.actions$
    .ofType(document.RETRIEVE_ALL_DELETED)
    .switchMap(() => {
      const errorMessage = 'Error retrieving deleted documents';
      return this.documentService.retrieveDeleted()
        .map(meta => new document.RetrieveAllDeletedCompleteAction(meta))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  removeDocument$: Observable<Action> = this.actions$
    .ofType(document.REMOVE_DOCUMENT)
    .map((a: document.RemoveDocumentAction) => a.payload)
    .switchMap(id => {
      const errorMessage = 'Error removing document';
      return this.documentService.remove(id)
        .map(meta => new document.RemoveDocumentCompleteAction(meta))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  removeDocumentComplete$: Observable<Action> = this.actions$
    .ofType(document.REMOVE_DOCUMENT_COMPLETE)
    .map((a: document.RemoveDocumentCompleteAction) => a.payload)
    .map(rsp => {
      const errorMessage = 'Error removing document';
      if (rsp === true) {
        return new document.RetrieveAllActiveAction();
      } else {
        return new auth.ErrorAction(new ErrorModel({}, errorMessage ));
      }
    });

  private saveFile(data: any, filename: string) {
    const blob = new Blob(data, { type: 'application/octet-stream'});
    if (navigator.appVersion.toString().indexOf('.NET') > 0) {
      window.navigator.msSaveBlob(blob, filename);
    } else {
      saveAs(blob, filename);
    }
  }

  private getFileUrl(data: any) {
    const blob = new Blob(data, { type: 'application/octet-stream' });
    return URL.createObjectURL(blob);
  }

  constructor(private actions$: Actions, private documentService: DocumentService, private store$: Store<fromRoot.State>) {}
}



// WEBPACK FOOTER //
// C:/Users/jstan/Projects/usda-document-client/src/app/modules/document/effects/document.ts